import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Injectable, Inject } from '@angular/core'
import {
  session_timer_reset,
  session_timer_warning,
  logout_user,
  initiate_login,
  login_failed,
  login_succeeded,
  initiate_logout,
  logout_succeeded,
  logout_failed,
  logout_aborted
} from '../actions/auth-action-types'
import { switchMap, map, tap, take, takeUntil, catchError, skipWhile, filter } from 'rxjs/operators'
import { timer, of, Subject, BehaviorSubject } from 'rxjs'
import { AuthService } from '../../auth.service'
import { BsModalService } from 'ngx-bootstrap/modal'
import { ConfirmModalComponent } from 'common-components'
import { OIDCClientConfig, OIDC_CLIENT_CONFIG } from './../../oidc-client-config'

@Injectable()
export class SessionEffects {
  private sessionMonitorEnabled: BehaviorSubject<boolean>

  constructor(
    @Inject(OIDC_CLIENT_CONFIG) private config: OIDCClientConfig,
    private action$: Actions,
    private authService: AuthService,
    private modalService: BsModalService
  ) {
    this.sessionMonitorEnabled = new BehaviorSubject<boolean>(true)
  }

  login_failed$ = createEffect(
    () =>
      this.action$.pipe(
        ofType(login_failed),
        tap(() => {
          this.sessionMonitorEnabled.next(false)
        })
      ),
    {
      dispatch: false
    }
  )

  initiate_logout$ = createEffect(() =>
    this.action$.pipe(
      ofType(initiate_logout),
      switchMap(action => {
        const modalRef = this.modalService.show(ConfirmModalComponent, {
          initialState: {
            title: 'Logout?',
            message: 'Are you sure you wish to logout?',
            autoDismiss: false
          }
        })

        const resultSubject: Subject<boolean> = modalRef.content.onClose as Subject<boolean>

        return resultSubject.asObservable().pipe(
          take(1),
          map(modalResult => ({ shouldContinue: modalResult, destination: action.destination }))
        )
      }),
      switchMap(modalResult => {
        if (modalResult.shouldContinue) {
          return this.authService.startLogout(window.location.pathname).pipe(
            map(() => logout_succeeded()),
            catchError(err => {
              console.log(err)
              return of(logout_failed())
            })
          )
        } else {
          return of(logout_aborted())
        }
      })
    )
  )

  initiate_login$ = createEffect(() =>
    this.action$.pipe(
      ofType(initiate_login),
      switchMap(action =>
        this.authService.startAuthentication(action.url).pipe(
          map(() => login_succeeded()),
          catchError((err, obs) => {
            console.log(err)
            return of(login_failed())
          })
        )
      ),
      catchError((err, obs) => {
        console.log(err)
        return of(login_failed())
      })
    )
  )

  session_timer_reset$ = createEffect(() =>
    this.action$.pipe(
      ofType(session_timer_reset),
      skipWhile(() => !this.sessionMonitorEnabled.getValue()),
      switchMap(() =>
        timer(this.config.timeoutIntervalInMilliseconds).pipe(
          takeUntil(
            this.sessionMonitorEnabled.pipe(
              filter(val => !val),
              tap(() => {
                console.log('monitor disabled!')
              })
            )
          ),
          map(() => session_timer_warning())
        )
      )
    )
  )

  session_timer_warning$ = createEffect(() =>
    this.action$.pipe(
      ofType(session_timer_warning),
      switchMap(() => {
        const modalRef = this.modalService.show(ConfirmModalComponent, {
          initialState: {
            title: 'Session Expiring',
            message: 'Your session is about to expire. Do you want to keep working?',
            autoDismiss: true
          }
        })

        const resultSubject: Subject<boolean> = modalRef.content.onClose as Subject<boolean>

        return resultSubject.asObservable().pipe(take(1))
      }),
      map(shouldContinue => {
        if (shouldContinue) {
          return session_timer_reset()
        } else {
          return logout_user()
        }
      })
    )
  )

  logout_user$ = createEffect(
    () =>
      this.action$.pipe(
        ofType(logout_user),
        tap(() => {
          this.authService.startLogout(window.location.pathname).pipe(take(1)).subscribe()
        })
      ),
    {
      dispatch: false
    }
  )
}
